import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { MemberService } from '../services/APIService';

// Async thunks for API calls
export const fetchMembers = createAsyncThunk(
  'members/fetchMembers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await MemberService.getAllMembers();
      // response is already { status, message, data: [...] }
      return response.data || [];
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const fetchMemberById = createAsyncThunk(
  'members/fetchMemberById',
  async (memberId, { rejectWithValue }) => {
    try {
      const response = await MemberService.getMember(memberId);
      // response is { status, message, data: {...} }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const createMember = createAsyncThunk(
  'members/createMember',
  async (memberData, { rejectWithValue }) => {
    try {
      const response = await MemberService.createMember(memberData);
      // response is { status, message, data: {...} }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const updateMember = createAsyncThunk(
  'members/updateMember',
  async ({ memberId, memberData }, { rejectWithValue }) => {
    try {
      const response = await MemberService.updateMember(memberId, memberData);
      // response is { status, message, data: {...} }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const deleteMember = createAsyncThunk(
  'members/deleteMember',
  async (memberId, { rejectWithValue }) => {
    try {
      await MemberService.deleteMember(memberId);
      return memberId;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

const initialState = {
  items: [],
  currentMember: null,
  loading: false,
  error: null,
  success: false,
};

const memberSlice = createSlice({
  name: 'members',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    resetCurrentMember: (state) => {
      state.currentMember = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Members
    builder.addCase(fetchMembers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMembers.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload;
      state.error = null;
    });
    builder.addCase(fetchMembers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Fetch Member by ID
    builder.addCase(fetchMemberById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMemberById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentMember = action.payload;
      state.error = null;
    });
    builder.addCase(fetchMemberById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create Member
    builder.addCase(createMember.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(createMember.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.items.push(action.payload);
      }
      state.success = true;
      state.error = null;
    });
    builder.addCase(createMember.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Update Member
    builder.addCase(updateMember.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(updateMember.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        const index = state.items.findIndex(m => m.id === action.payload.id);
        if (index > -1) {
          state.items[index] = { ...state.items[index], ...action.payload };
        }
      }
      state.success = true;
      state.error = null;
    });
    builder.addCase(updateMember.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Delete Member
    builder.addCase(deleteMember.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(deleteMember.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter(m => m.id !== action.payload);
      state.success = true;
      state.error = null;
    });
    builder.addCase(deleteMember.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });
  },
});

export const { clearError, clearSuccess, resetCurrentMember } = memberSlice.actions;
export default memberSlice.reducer;
